<template>
  <div class="pop-up" v-if="active" @mousedown="toggleVisibility">
    <div v-if="!success" class="ds-card" @mousedown.stop>
      <svg
        @click="toggleVisibility"
        class="close"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M15.4427 5.442C15.6868 5.19791 15.6868 4.80216 15.4427 4.55807C15.1986 4.31398 14.8029 4.31398 14.5588 4.55807L10.0005 9.11634L5.44225 4.55807C5.19816 4.31398 4.80241 4.31398 4.55832 4.55807C4.31423 4.80216 4.31423 5.19791 4.55832 5.442L9.11658 10.0003L4.55831 14.5586C4.31422 14.8026 4.31422 15.1984 4.55831 15.4425C4.8024 15.6866 5.19815 15.6866 5.44224 15.4425L10.0005 10.8842L14.5588 15.4425C14.8029 15.6866 15.1986 15.6866 15.4427 15.4425C15.6868 15.1984 15.6868 14.8027 15.4427 14.5586L10.8844 10.0003L15.4427 5.442Z"
          fill="#B2C0CD"
        />
      </svg>
      <h2 class="ds-card__title">{{ $t('d_studio_card_title') }}</h2>
      <h3 class="ds-card__subtitle">
        {{ $t('d_studio_card_subtitle') }}
      </h3>
      <form @submit.prevent="submitForm">
        <div class="input-container">
          <ui-input
            v-model="formData.first_name"
            input-class="input"
            class="input-settings"
            type="text"
            :placeholder="$t('d_studio_input_name')"
            @blur="v$.formData.first_name.$touch()"
            :error="v$.formData.first_name.$error"
            :clearable="true"
          />
          <ui-input
            v-model="formData.phone"
            input-class="input"
            class="input-settings"
            type="number"
            :placeholder="$t('d_studio_input_phone')"
            :spin="true"
            @blur="v$.formData.phone.$touch()"
            :error="v$.formData.phone.$error"
            :clearable="true"
          />
          <ui-input
            v-model="formData.email"
            input-class="input"
            class="input-settings"
            type="text"
            :placeholder="$t('d_studio_input_mail')"
            :clearable="true"
            @blur="v$.formData.email.$touch()"
            :error="v$.formData.email.$error"
          />
        </div>
        <div class="button-container">
          <ui-button
            class="ds-card__back"
            color="gray"
            :size="'b_m'"
            :label="$t('d_studio_back')"
            @click="toggleVisibility"
          />
          <ui-button
            class="ds-card__submit"
            color="primary"
            :size="'b_m'"
            :label="$t('d_studio_send_request')"
            type="submit"
            :isLoading="disabled"
          />
        </div>
      </form>
    </div>
    <div v-else class="ds-success" @mousedown.stop>
      <svg
        @click="closeSuccess"
        class="close"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M15.4427 5.442C15.6868 5.19791 15.6868 4.80216 15.4427 4.55807C15.1986 4.31398 14.8029 4.31398 14.5588 4.55807L10.0005 9.11634L5.44225 4.55807C5.19816 4.31398 4.80241 4.31398 4.55832 4.55807C4.31423 4.80216 4.31423 5.19791 4.55832 5.442L9.11658 10.0003L4.55831 14.5586C4.31422 14.8026 4.31422 15.1984 4.55831 15.4425C4.8024 15.6866 5.19815 15.6866 5.44224 15.4425L10.0005 10.8842L14.5588 15.4425C14.8029 15.6866 15.1986 15.6866 15.4427 15.4425C15.6868 15.1984 15.6868 14.8027 15.4427 14.5586L10.8844 10.0003L15.4427 5.442Z"
          fill="#B2C0CD"
        />
      </svg>
      <h2 class="ds-success__title">
        {{ $t('d_studio_card_title') }}
      </h2>
      <div class="ds-success__desc">
        {{ $t('d_studio_success_desc') }}
      </div>
      <ui-button
        class="ds-success__btn"
        color="primary"
        :size="'b_m'"
        @click="closeSuccess"
        :label="$t('d_studio_close')"
      />
    </div>
  </div>
</template>

<script setup>
import {helpers, required, email} from '@vuelidate/validators';
import {useVuelidate} from '@vuelidate/core';
import {useI18n} from 'vue-i18n';

const emits = defineEmits(['close', 'success']);
const props = defineProps({
  active: Boolean,
});
const formData = ref({
  first_name: '',
  phone: '',
  email: '',
});
const success = ref(false);
const disabled = ref(false);
const {locale} = useI18n();

const toggleVisibility = () => {
  emits('close');
};

const closeSuccess = () => {
  success.value = false;
  formData.value = {
    first_name: '',
    phone: '',
    email: '',
  };
  toggleVisibility();
};

const phoneLength = (value) => {
  const phone = value.replace(/\D/g, ''); // Удаление всех нечисловых символов
  return phone.length >= 8 && phone.length <= 14;
};

const nameLength = (value) => {
  return value.trim().length >= 3;
};

const validations = {
  formData: {
    first_name: {
      required: helpers.withMessage(
        'Имя обязательно для заполнения',
        required,
      ),
      nameLength: helpers.withMessage(
        'Имя должно содержать минимум 3 символа',
        nameLength,
      ),
    },
    email: {
      required: helpers.withMessage(
        'Email обязателен для заполнения',
        required,
      ),
      email,
    },
    phone: {
      required: helpers.withMessage(
        'Номер телефона обязателен для заполнения',
        required,
      ),
      phoneLength: helpers.withMessage(
        'Номер телефона должен содержать от 8 до 14 цифр',
        phoneLength,
      ),
    },
  },
};

const v$ = useVuelidate(validations, {formData});

const submitForm = async () => {
  v$.value.$touch();
  if (!v$.value.$invalid) {
    disabled.value = true;
    const {data, error} = await sendFormData(formData.value);
    if (!error) {
      success.value = true;
      disabled.value = false;
      emits('success');
    }
  }
};

const sendFormData = async (formData) => {
  try {
    formData['language'] = locale.value;
    const response = await fetch(
      'https://dreamflat.design/proxy/design/send_mail',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      return {data: undefined, error: true};
    }

    const responseData = await response.json();
    return {data: responseData, error: false};
  } catch (error) {
    return {data: undefined, error: true};
  }
};
</script>

<style scoped lang="scss">
.pop-up {
  position: fixed;
  width: 100vw;
  height: 100vh;
  z-index: 100000;

  top: 0;
  left: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  background: rgba(32, 48, 64, 0.75);

  @media (max-width: 600px) {
    align-items: flex-end;
  }
}

.ds-card {
  background: var(--var-white-000);
  position: relative;

  max-width: 448px;

  box-sizing: border-box;
  padding: 24px;
  border-radius: 12px;

  @media (max-width: 600px) {
    max-width: initial;
    width: 100%;
    border-radius: 24px 24px 0 0;
    padding: 24px 24px 64px 24px;
  }
}

.ds-card__title {
  margin: 0;

  //styleName: Titles 2;
  font-family: Segoe UI;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0.02em;
  text-align: left;
}

.ds-card__subtitle {
  margin-top: 8px;
  margin-bottom: 16px;

  //styleName: Launcher/P3 (body);
  font-family: Segoe UI;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.02em;
  text-align: left;
}

.close {
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;

  @media (max-width: 600px) {
    top: 16px;
    right: 16px;
  }
}

.input-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.button-container {
  margin-top: 32px;
  display: flex;
  justify-content: space-between;
}

.ds-card__back {
  background: white;
  border: 1px solid #e1e5f0;
  color: #64686e !important;

  @media (max-width: 600px) {
    display: none;
  }
}

.ds-card__submit {
  @media (max-width: 600px) {
    width: 100%;
  }
}

.ds-success {
  max-width: 448px;
  box-sizing: border-box;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: #fbfaff;
  border-radius: 12px;
  justify-content: space-between;
  align-self: center;
  position: relative;
  &__title {
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
    letter-spacing: 0.02em;
    text-align: left;
    margin-top: 0;
    margin-bottom: 16px;
    color: #3d424a;
  }
  &__desc {
    font-size: 16px;
    font-weight: 400;
    line-height: 22.4px;
    letter-spacing: 0.03em;
    text-align: left;
    margin-bottom: 32px;
    color: #64686e;
  }
  .ds-success__btn {
    height: 32px;
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
    letter-spacing: 0.02em;
    border-radius: 6px;
  }
}

.ds-success__subtitle {
  //styleName: Launcher/P1;
  font-family: Segoe UI;
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  letter-spacing: 0.03em;
  text-align: center;
}
</style>
